import { WeightValueEntry, WeightValues } from "@/types/dataset";
import { ScoringOptions, ScoringOptionsEnum } from "@/types/spatial";
import { getIsIncludedScoring } from "@/utils/weightValues";

import { initializeDatasetWeights } from "./utils";

export type WeightSettingsMode = "simple" | "advanced";

export interface WeightSettingsState {
  localWeightValues: WeightValues;
  selectedScoringOption: ScoringOptions | null;
  mode: WeightSettingsMode;
  /** Advanced-normalization intercept. Seeded from spatialConfig.intercept and
   * persisted back on save; emitted on the wire under LOG_Z_SCORE.
   */
  intercept: string;
  /** Set once the user attempts to Save in advanced mode. Advanced Mean/Stdv
   * inputs only show their red-border errors after this — never on first open.
   * Reset whenever the mode changes so re-entering advanced starts clean.
   */
  hasAttemptedAdvancedSave: boolean;
}

export type WeightSettingsAction =
  | { type: "weightValues/syncFromStore"; weightValues: WeightValues }
  | {
      type: "spatialConfig/syncFromStore";
      scoringOption: ScoringOptions | null;
      intercept?: string;
    }
  | {
      type: "dataset/setWeight";
      datasetId: string;
      value: string;
      // POI datasets expose a primary attribute that mirrors the dataset weight.
      isPoi?: boolean;
      primaryAttributeId?: string;
    }
  | {
      type: "dataset/toggleInclude";
      datasetId: string;
      included: boolean;
      // For POI, only the primary attribute follows the dataset toggle.
      isPoi?: boolean;
      primaryAttributeId?: string;
    }
  | {
      type: "attribute/setWeight";
      datasetId: string;
      attributeId: string;
      value: string;
    }
  | {
      type: "attribute/setNegativeImpact";
      datasetId: string;
      attributeId: string;
      negativeImpact: boolean;
    }
  | {
      /** Advanced-normalization per-attribute stats. Each input dispatches only
       * the field it changed; the other is left untouched. For POI datasets the
       * caller passes the primary attribute id (the mean/stdv inputs sit at the
       * dataset level and mirror onto the primary attribute, like the weight).
       */
      type: "attribute/setZScore";
      datasetId: string;
      attributeId: string;
      logMean?: string;
      logStdDev?: string;
    }
  | {
      type: "attribute/toggleInclude";
      datasetId: string;
      attributeId: string;
      included: boolean;
    }
  | { type: "scoringOption/set"; scoringOption: ScoringOptions | null }
  | { type: "mode/set"; mode: WeightSettingsMode }
  | { type: "intercept/set"; intercept: string }
  | { type: "advanced/markValidationAttempted" };

const EMPTY_DATASET = { weight: "", attributes: {} };

/**
 * Converts a weight value entry to a weight value entry with a default weight of 0.
 */
const toEntry = (prev?: WeightValueEntry): WeightValueEntry =>
  prev && typeof prev === "object" ? { ...prev } : { weight: "0" };

const updateAttribute = (
  weightValues: WeightValues,
  datasetId: string,
  attributeId: string,
  updater: (prev?: WeightValueEntry) => WeightValueEntry
): WeightValues => {
  const prevDataset = weightValues[datasetId] || EMPTY_DATASET;
  return {
    ...weightValues,
    [datasetId]: {
      ...prevDataset,
      attributes: {
        ...prevDataset.attributes,
        [attributeId]: updater(prevDataset.attributes[attributeId])
      }
    }
  };
};

export const createInitialWeightSettingsState = ({
  weightValues,
  scoringOption,
  intercept
}: {
  weightValues: WeightValues;
  scoringOption: ScoringOptions | null;
  intercept?: string;
}): WeightSettingsState => {
  // LOG_Z_SCORE is the persisted "advanced normalization" signal. It is
  // wire-only — the radio always shows Normalization (NORMALIZED) — so when the
  // saved option is LOG_Z_SCORE we open directly in advanced mode and keep the
  // selected scoring option as NORMALIZED.
  const isAdvanced = scoringOption === ScoringOptionsEnum.LOG_Z_SCORE;
  return {
    localWeightValues: initializeDatasetWeights(weightValues),
    selectedScoringOption: isAdvanced
      ? ScoringOptionsEnum.NORMALIZED
      : scoringOption || ScoringOptionsEnum.NORMALIZED,
    mode: isAdvanced ? "advanced" : "simple",
    intercept: intercept ?? "",
    hasAttemptedAdvancedSave: false
  };
};

export const weightSettingsReducer = (
  state: WeightSettingsState,
  action: WeightSettingsAction
): WeightSettingsState => {
  switch (action.type) {
    // Local edits always work on a cloned copy so the Redux object is never
    // shared (used both for the reset action and store-change syncs).
    case "weightValues/syncFromStore":
      return {
        ...state,
        localWeightValues: initializeDatasetWeights(action.weightValues)
      };

    case "spatialConfig/syncFromStore": {
      const isAdvanced =
        action.scoringOption === ScoringOptionsEnum.LOG_Z_SCORE;
      return {
        ...state,
        selectedScoringOption: isAdvanced
          ? ScoringOptionsEnum.NORMALIZED
          : action.scoringOption || ScoringOptionsEnum.NORMALIZED,
        mode: isAdvanced ? "advanced" : "simple",
        intercept: action.intercept ?? "",
        hasAttemptedAdvancedSave: false
      };
    }

    case "dataset/setWeight": {
      const prevDataset =
        state.localWeightValues[action.datasetId] || EMPTY_DATASET;
      const attributeId =
        action.primaryAttributeId || Object.keys(prevDataset.attributes)[0];
      const mirroredAttribute: WeightValueEntry = {
        ...toEntry(prevDataset.attributes[attributeId]),
        weight: action.value
      };
      // Hidden attributes are preserved — only the mirrored one is replaced.
      const nextAttributes = {
        ...prevDataset.attributes,
        ...(attributeId ? { [attributeId]: mirroredAttribute } : {})
      };
      return {
        ...state,
        localWeightValues: {
          ...state.localWeightValues,
          [action.datasetId]: {
            ...prevDataset,
            weight: action.value,
            attributes: action.isPoi ? nextAttributes : prevDataset.attributes
          }
        }
      };
    }

    case "dataset/toggleInclude": {
      const prevDataset =
        state.localWeightValues[action.datasetId] || EMPTY_DATASET;
      if (!action.isPoi) {
        // Non-POI datasets cascade the toggle to every attribute.
        const nextAttributes = Object.fromEntries(
          Object.entries(prevDataset.attributes).map(([attributeId, entry]) => [
            attributeId,
            {
              ...toEntry(entry),
              include_scoring: action.included
            }
          ])
        );

        return {
          ...state,
          localWeightValues: {
            ...state.localWeightValues,
            [action.datasetId]: {
              ...prevDataset,
              include_scoring: action.included,
              attributes: nextAttributes
            }
          }
        };
      }

      // For POI, only the primary attribute follows the dataset toggle.
      const primaryAttributeId =
        action.primaryAttributeId || Object.keys(prevDataset.attributes)[0];
      const nextAttributes = Object.fromEntries(
        Object.entries(prevDataset.attributes).map(([attributeId, entry]) => [
          attributeId,
          {
            ...toEntry(entry),
            include_scoring:
              attributeId === primaryAttributeId && action.included
          }
        ])
      );

      if (primaryAttributeId && !nextAttributes[primaryAttributeId]) {
        nextAttributes[primaryAttributeId] = {
          weight: prevDataset.weight,
          include_scoring: action.included
        };
      }

      return {
        ...state,
        localWeightValues: {
          ...state.localWeightValues,
          [action.datasetId]: {
            ...prevDataset,
            include_scoring: action.included,
            attributes: nextAttributes
          }
        }
      };
    }

    case "attribute/setWeight":
      return {
        ...state,
        localWeightValues: updateAttribute(
          state.localWeightValues,
          action.datasetId,
          action.attributeId,
          (prev) => ({ ...toEntry(prev), weight: action.value })
        )
      };

    case "attribute/setNegativeImpact":
      return {
        ...state,
        localWeightValues: updateAttribute(
          state.localWeightValues,
          action.datasetId,
          action.attributeId,
          (prev) => {
            const base = toEntry(prev);
            if (action.negativeImpact) {
              return { ...base, negative_impact: true };
            }
            // The flag is removed (not set to false) so the saved payload
            // matches the legacy shape, where absence means "no impact".
            const { negative_impact: _removed, ...rest } = base;
            return rest;
          }
        )
      };

    case "attribute/setZScore":
      return {
        ...state,
        localWeightValues: updateAttribute(
          state.localWeightValues,
          action.datasetId,
          action.attributeId,
          (prev) => {
            const base = toEntry(prev);
            const nextZScore = {
              log_mean: action.logMean ?? base.z_score?.log_mean ?? "",
              log_std_dev: action.logStdDev ?? base.z_score?.log_std_dev ?? ""
            };
            // Drop the field entirely once both inputs are cleared, so the
            // saved/submitted payload matches the legacy "absent" shape.
            if (
              nextZScore.log_mean.trim() === "" &&
              nextZScore.log_std_dev.trim() === ""
            ) {
              const { z_score: _removed, ...rest } = base;
              return rest;
            }
            return { ...base, z_score: nextZScore };
          }
        )
      };

    case "attribute/toggleInclude": {
      const prevDataset = state.localWeightValues[action.datasetId] || {
        ...EMPTY_DATASET
      };
      const nextAttributes = {
        ...prevDataset.attributes,
        [action.attributeId]: {
          ...toEntry(prevDataset.attributes[action.attributeId]),
          include_scoring: action.included
        }
      };

      // Auto-disable parent when no attribute remains included.
      // Auto-enable parent when toggling any child ON.
      const anyIncluded = Object.values(nextAttributes).some((entry) =>
        getIsIncludedScoring(entry)
      );

      return {
        ...state,
        localWeightValues: {
          ...state.localWeightValues,
          [action.datasetId]: {
            ...prevDataset,
            include_scoring: action.included ? true : anyIncluded,
            attributes: nextAttributes
          }
        }
      };
    }

    case "scoringOption/set":
      return { ...state, selectedScoringOption: action.scoringOption };

    case "mode/set":
      // Reset the attempt flag so errors never carry across a mode switch.
      return { ...state, mode: action.mode, hasAttemptedAdvancedSave: false };

    case "intercept/set":
      return { ...state, intercept: action.intercept };

    case "advanced/markValidationAttempted":
      return { ...state, hasAttemptedAdvancedSave: true };
  }
};
